import {
  AdminAnimation,
  AdminSpinner,
  AdminWalletBankResponse,
  Button,
  Div,
  ModalPortal,
  P,
  Radio,
  User,
  UserRole,
  WalletBankResponse,
  WalletBankV2Response,
  Wallets,
} from '@vgrubs/components';
import { Step, useWalletV2 } from '../../hooks/useWalletV2';
import { AboutYou } from './AboutYou';
import { BankInfo } from './BankInfo';
import { BankTerms } from './BankTerms';
import { BusinessInfo } from './BusinessInfo';
import { Confirmation } from './Confirmation';
import { handleNextButtonText, handleSubtitle, handleTitle } from './helpers';
import { BankConnected } from './BankConnected';
import { AnimatePresence } from 'framer-motion';
import { Dispatch, SetStateAction } from 'react';

export const BankV2 = ({
  userId,
  userRole,
  modalIsOpen,
  setModalIsOpen,
  wallet,
  setWallet,
  user,
}: {
  userId?: number;
  userRole: UserRole;
  modalIsOpen: Wallets | boolean | null;
  setModalIsOpen: Dispatch<SetStateAction<Wallets | boolean | null>>;
  wallet: Wallets | AdminWalletBankResponse | WalletBankResponse;
  setWallet: Dispatch<
    SetStateAction<
      WalletBankV2Response | AdminWalletBankResponse | WalletBankResponse | null
    >
  >;
  user?: User;
}) => {
  const {
    termsIsOpen,
    setTermsIsOpen,
    handleBack,
    handleNext,
    firstName,
    lastName,
    email,
    businessName,
    phoneNumber,
    addressObject,
    formattedAddress,
    ssn,
    step,
    setFirstName,
    setLastName,
    setEmail,
    setBusinessName,
    setPhoneNumber,
    setAccountType,
    accountType,
    setSsn,
    ein,
    setEin,
    taxClassification,
    setTaxClassification,
    setTermsAgreed,
    termsAgreed,
    routingNumber,
    setRoutingNumber,
    accountNumber,
    setAccountNumber,
    businessType,
    setBusinessType,
    setLoading,
    loading,
    setAddressObject,
    bankConnectedIsOpen,
    setBankConnectedIsOpen,
    confirmAccountNumber,
    setConfirmAccountNumber,
  } = useWalletV2({
    setModalIsOpen,
    wallet,
    setWallet,
    modalIsOpen,
    userId,
    userRole,
    user,
  });

  return (
    <>
      {loading && <AdminSpinner />}
      <ModalPortal
        isOpen={!!modalIsOpen && !termsIsOpen}
        closeModal={() => setModalIsOpen(null)}
        logo={false}
        header={handleTitle(step)}
        subtitle={handleSubtitle(step, accountType)}
        onBack={handleBack}
        maxHeight="calc(90vh - 85px)"
        minHeight="calc(90vh - 85px)"
        maxHeightSM="calc(80vh - 85px)"
        minHeightSM="calc(80vh - 85px)"
        $numOfFloatingButtons={1}
        footer={
          <Div>
            {step === Step.BankInfo && (
              <P $center $mb="16">
                Checking accounts only. Savings not supported.
              </P>
            )}
            {step === Step.Confirmation && (
              <Div $flex $center $gap="12px" $mb="16">
                <Div onClick={() => setTermsAgreed(!termsAgreed)}>
                  <Radio checked={termsAgreed} />
                </Div>
                <P $m="0">
                  I agree with{' '}
                  <span
                    onClick={() => {
                      setTermsIsOpen(true);
                    }}
                    style={{ textDecoration: 'underline', cursor: 'pointer' }}
                  >
                    vGrubs Terms
                  </span>
                </P>
              </Div>
            )}
            <Button $w="100%" $selected onClick={handleNext}>
              {handleNextButtonText(step)}
            </Button>
          </Div>
        }
      >
        <AnimatePresence mode="wait" initial={false}>
          <AdminAnimation key={step}>
            {step === Step.AboutYou && (
              <AboutYou
                firstName={firstName}
                lastName={lastName}
                email={email}
                setFirstName={setFirstName}
                setLastName={setLastName}
                setEmail={setEmail}
                phoneNumber={phoneNumber}
                setPhoneNumber={setPhoneNumber}
                accountType={accountType}
                setAccountType={setAccountType}
                ssn={ssn}
                setSsn={setSsn}
                addressObject={addressObject}
                setAddressObject={setAddressObject}
                formattedAddress={formattedAddress}
                setLoading={setLoading}
              />
            )}
            {step === Step.BusinessInfo && (
              <BusinessInfo
                businessName={businessName}
                setBusinessName={setBusinessName}
                ssn={ssn}
                setSsn={setSsn}
                ein={ein}
                setEin={setEin}
                taxClassification={taxClassification}
                setTaxClassification={setTaxClassification}
                businessType={businessType}
                setBusinessType={setBusinessType}
              />
            )}
            {step === Step.BankInfo && (
              <BankInfo
                routingNumber={routingNumber}
                setRoutingNumber={setRoutingNumber}
                accountNumber={accountNumber}
                setAccountNumber={setAccountNumber}
                confirmAccountNumber={confirmAccountNumber}
                setConfirmAccountNumber={setConfirmAccountNumber}
              />
            )}
            {step === Step.Confirmation && (
              <Confirmation
                taxClassification={taxClassification}
                accountNumber={accountNumber}
                routingNumber={routingNumber}
                accountType={accountType}
                businessName={businessName}
                address={formattedAddress}
              />
            )}
          </AdminAnimation>
        </AnimatePresence>
      </ModalPortal>
      <BankTerms
        isOpen={termsIsOpen}
        onBack={() => {
          setTermsIsOpen(false);
        }}
        onAgree={() => {
          setTermsIsOpen(false);
          setTermsAgreed(true);
        }}
      />
      <BankConnected
        isOpen={bankConnectedIsOpen}
        setIsOpen={setBankConnectedIsOpen}
      />
    </>
  );
};
